import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputRightAddon,
  Radio,
  RadioGroup,
  Select,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { useLoggedInWithOrgContextState } from "../../../../../common/auth/useLoggedInWithOrgContextState";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaTrash } from "react-icons/fa";
import {
  AwardCriterionType,
  PersonSearchCapability,
  Currency,
  type DesignContestClimateConsiderationDto,
  type DesignContestDto,
  type DesignContestJuryMemberDto,
  type DesignContestPrizeDto,
  type FoaPart,
  type PersonDto,
  ProcedureType,
  Regulation,
  type SourcingEventDto,
  type SourcingEventPatchDto,
} from "../../../../../autogen/bff-api";
import { useEditor } from "../../../../../common/editing/save-coordinator";
import { useFieldErrorTranslation } from "../../../../../common/editing/save-coordinator/useFieldErrorTranslation";
import { NumericInput } from "../../../../../common/input/NumericInput";
import { PersonSelector } from "../../../../../common/persons/PersonSelector";
import {
  announcesDesignContestNationally,
  DESIGN_CONTEST_CLIMATE_TYPES,
  holdsQualifiedThird,
} from "../utils/designContest";
import { exemptionTitleKey } from "../utils/environmentJustificationTemplates";

const TEXT_DEBOUNCE_MS = 400;
const FIELD_PATH = "publicContext.designContest";

type ContestDraft = DesignContestDto &
  Required<Pick<DesignContestDto, "prizes" | "juryCriteria" | "jury">>;

const editableOf = (contest: ContestDraft) => ({
  leadsToServiceContract: contest.leadsToServiceContract,
  juryDecisionBinding: contest.juryDecisionBinding ?? null,
  prizes: contest.prizes,
  juryCriteria: contest.juryCriteria,
  requiredParticipantQualification: contest.requiredParticipantQualification,
  jury: contest.jury,
  climateConsideration: contest.climateConsideration ?? null,
});

// t('Climate and environmental requirements in the competition programme')
const climateTypeLabelKey = (type: AwardCriterionType): string =>
  type === AwardCriterionType.QualityEnvironmentalSpecification
    ? "Climate and environmental requirements in the competition programme"
    : exemptionTitleKey(type);

const asDraft = (contest: DesignContestDto | null): ContestDraft => ({
  ...contest,
  prizes: contest?.prizes ?? [],
  juryCriteria: contest?.juryCriteria ?? [],
  jury: contest?.jury ?? [],
});

interface Props {
  eventId: string;
  contest: DesignContestDto | null;
  procedureType: ProcedureType;
  regulation: Regulation | null;
  foaPart?: FoaPart | null;
  homeDepartmentId?: string | null;
  isEditable: boolean;
}

export const DesignContestField = ({
  eventId,
  contest,
  procedureType,
  regulation,
  foaPart = null,
  homeDepartmentId = null,
  isEditable,
}: Props) => {
  const { t } = useTranslation();
  const buyerOrganizationId = useLoggedInWithOrgContextState().selectedOrg.id;
  const { updateField, fieldStatus, fieldError } = useEditor<SourcingEventDto, SourcingEventPatchDto>(eventId);
  const formatFieldError = useFieldErrorTranslation();
  const [draft, setDraft] = useState<ContestDraft>(asDraft(contest));
  const status = fieldStatus(FIELD_PATH);
  const isSaving = status === "saving" || status === "pending";
  const savedJson = JSON.stringify(asDraft(contest));
  useEffect(() => {
    if (!isSaving) setDraft(JSON.parse(savedJson) as ContestDraft);
  }, [savedJson, isSaving]);
  const underUtilities = regulation === Regulation.Forsyningsforskriften;
  const qualificationSet = !!draft.requiredParticipantQualification?.trim();

  const save = (changes: Partial<ContestDraft>, debounceMs?: number) => {
    const next = { ...draft, ...changes };
    setDraft(next);
    updateField({ publicContext: { designContest: editableOf(next) } }, debounceMs ? { debounceMs } : undefined);
  };
  const setCriterion = (index: number, value: string) =>
    save({ juryCriteria: draft.juryCriteria.map((c, i) => (i === index ? value : c)) }, TEXT_DEBOUNCE_MS);
  const setPrize = (index: number, changes: Partial<DesignContestPrizeDto>, debounceMs?: number) =>
    save({ prizes: draft.prizes.map((p, i) => (i === index ? { ...p, ...changes } : p)) }, debounceMs);
  const setMember = (index: number, changes: Partial<DesignContestJuryMemberDto>, debounceMs?: number) =>
    save({ jury: draft.jury.map((m, i) => (i === index ? { ...m, ...changes } : m)) }, debounceMs);
  const national = announcesDesignContestNationally(regulation, foaPart);
  const setClimate = (changes: Partial<DesignContestClimateConsiderationDto>, debounceMs?: number) => {
    const current = draft.climateConsideration;
    const type = changes.type ?? current?.type;
    save(
      { climateConsideration: type ? { type, description: current?.description ?? null, ...changes } : null },
      debounceMs,
    );
  };
  const error = (field: string) => fieldError(`${FIELD_PATH}.${field}`);
  const errorText = (field: string): string | null => {
    const found = error(field);
    return found ? formatFieldError(found) : null;
  };

  return (
    <Box borderWidth="1px" borderColor="smBorder" borderRadius="lg" bg="white" p="5">
      <Heading as="h3" size="sm">
        {t("Design contest")} {isSaving && <Spinner size="xs" />}
      </Heading>
      <Text fontSize="sm" color="smMuted" mt="1">
        {t("A jury assesses the entries anonymously and chooses the winners.")}{" "}
        <Text as="span" fontStyle="italic">
          {underUtilities ? "forsyningsforskriften kap. 27" : "FOA kap. 31"}
        </Text>
      </Text>

      <FormControl mt="5" isDisabled={!isEditable}>
        <FormLabel fontSize="sm">{t("Who can take part?")}</FormLabel>
        <RadioGroup
          value={procedureType}
          onChange={(value) => updateField({ publicContext: { procedureType: value as ProcedureType } })}
        >
          <Stack spacing="2">
            <Radio value={ProcedureType.DesignContestOpen} colorScheme="teal">
              <Text fontSize="sm">{t("Everyone who wants to may submit an entry")}</Text>
            </Radio>
            <Radio value={ProcedureType.DesignContestRestricted} colorScheme="teal">
              <Text fontSize="sm">{t("Only participants chosen after a request to participate")}</Text>
            </Radio>
          </Stack>
        </RadioGroup>
      </FormControl>

      <FormControl mt="5" isDisabled={!isEditable} isRequired isInvalid={!!error("leadsToServiceContract")}>
        <FormLabel fontSize="sm">{t("Will a service contract follow the contest?")}</FormLabel>
        <RadioGroup
          value={draft.leadsToServiceContract == null ? "" : String(draft.leadsToServiceContract)}
          onChange={(value) => save({ leadsToServiceContract: value === "true" })}
        >
          <Stack spacing="2">
            <Radio value="true" colorScheme="teal">
              <Text fontSize="sm">{t("Yes, with one of the winners")}</Text>
            </Radio>
            <Radio value="false" colorScheme="teal">
              <Text fontSize="sm">{t("No, the contest awards prizes or payments only")}</Text>
            </Radio>
          </Stack>
        </RadioGroup>
        <FormHelperText>{t("Stated in the notice, so the winners can be invited without a new competition.")}</FormHelperText>
      </FormControl>

      <FormControl mt="5" isDisabled={!isEditable}>
        <Checkbox
          isChecked={draft.juryDecisionBinding !== false}
          onChange={(event) => save({ juryDecisionBinding: event.target.checked })}
          colorScheme="teal"
        >
          <Text fontSize="sm">{t("The jury's decision is binding on the contracting authority")}</Text>
        </Checkbox>
        <FormHelperText>
          {t("Stated in the notice. Untick it if the jury only gives an opinion and you decide yourselves who wins.")}
        </FormHelperText>
      </FormControl>

      <FormControl mt="5" isDisabled={!isEditable} isRequired isInvalid={!!error("juryCriteria")}>
        <FormLabel fontSize="sm">{t("The jury's evaluation criteria")}</FormLabel>
        <Stack spacing="2">
          {draft.juryCriteria.map((criterion, index) => (
            <HStack key={index}>
              <Input
                size="sm"
                value={criterion}
                aria-label={t("Criterion {{number}}", { number: index + 1 }) ?? undefined}
                onChange={(event) => setCriterion(index, event.target.value)}
              />
              <IconButton
                size="sm"
                variant="ghost"
                aria-label={t("Remove") ?? undefined}
                icon={<FaTrash />}
                isDisabled={!isEditable}
                onClick={() => save({ juryCriteria: draft.juryCriteria.filter((_, i) => i !== index) })}
              />
            </HStack>
          ))}
        </Stack>
        <Button
          size="sm"
          variant="outline"
          mt="2"
          isDisabled={!isEditable}
          onClick={() => save({ juryCriteria: [...draft.juryCriteria, ""] })}
        >
          {t("Add criterion")}
        </Button>
        <FormHelperText>{t("Published in the notice. The jury assesses the entries against these only.")}</FormHelperText>
        {errorText("juryCriteria") && <FormErrorMessage>{errorText("juryCriteria")}</FormErrorMessage>}
      </FormControl>

      {national && (
        <FormControl mt="5" isDisabled={!isEditable} isInvalid={!!error("climateConsideration")}>
          <FormLabel fontSize="sm">{t("Climate and environmental considerations")}</FormLabel>
          <Select
            size="sm"
            value={draft.climateConsideration?.type ?? ""}
            placeholder={t("Choose how the contest takes climate and environmental considerations into account") ?? undefined}
            aria-label={t("Climate and environmental considerations") ?? undefined}
            onChange={(event) =>
              event.target.value
                ? setClimate({ type: event.target.value as AwardCriterionType })
                : save({ climateConsideration: null })
            }
          >
            {DESIGN_CONTEST_CLIMATE_TYPES.map((type) => (
              <option key={type} value={type}>
                {t(climateTypeLabelKey(type))}
              </option>
            ))}
          </Select>
          <FormHelperText>
            {t("The national notice states this when the jury has two or more criteria (anskaffelsesloven § 5b).")}
          </FormHelperText>
          {errorText("climateConsideration") && <FormErrorMessage>{errorText("climateConsideration")}</FormErrorMessage>}
        </FormControl>
      )}

      {national && draft.climateConsideration && (
        <FormControl mt="3" isDisabled={!isEditable} isRequired>
          <FormLabel fontSize="sm">
            {t("How the contest takes climate and environmental considerations into account, or why it is exempt")}
          </FormLabel>
          <Textarea
            size="sm"
            value={draft.climateConsideration.description ?? ""}
            onChange={(event) => setClimate({ description: event.target.value }, TEXT_DEBOUNCE_MS)}
          />
        </FormControl>
      )}

      <FormControl mt="5" isDisabled={!isEditable}>
        <FormLabel fontSize="sm">{t("Professional qualification required of the participants")}</FormLabel>
        <Input
          size="sm"
          value={draft.requiredParticipantQualification ?? ""}
          placeholder={t("For example: architect") ?? undefined}
          onChange={(event) => save({ requiredParticipantQualification: event.target.value }, TEXT_DEBOUNCE_MS)}
        />
        <FormHelperText>
          {t("Leave empty if none is required. If one is, at least a third of the jury must hold it as well.")}
        </FormHelperText>
      </FormControl>

      <FormControl mt="5" isDisabled={!isEditable} isInvalid={!!error("prizes")}>
        <FormLabel fontSize="sm">{t("Prizes and payments")}</FormLabel>
        <Stack spacing="2">
          {draft.prizes.map((prize, index) => (
            <SimpleGrid key={index} columns={{ base: 1, md: 3 }} spacing="2" alignItems="center">
              <Input
                size="sm"
                gridColumn={{ md: "span 2" }}
                value={prize.description}
                placeholder={t("For example: 1st prize") ?? undefined}
                aria-label={t("Prize {{number}}", { number: index + 1 }) ?? undefined}
                onChange={(event) => setPrize(index, { description: event.target.value }, TEXT_DEBOUNCE_MS)}
              />
              <HStack>
                <InputGroup size="sm">
                  <NumericInput
                    numericValue={prize.value?.value ?? null}
                    aria-label={t("Value in NOK") ?? undefined}
                    onNumericChange={(value) =>
                      setPrize(
                        index,
                        { value: value == null ? null : { value, currency: Currency.Nok } },
                        TEXT_DEBOUNCE_MS,
                      )
                    }
                  />
                  <InputRightAddon>NOK</InputRightAddon>
                </InputGroup>
                <IconButton
                  size="sm"
                  variant="ghost"
                  aria-label={t("Remove") ?? undefined}
                  icon={<FaTrash />}
                  isDisabled={!isEditable}
                  onClick={() => save({ prizes: draft.prizes.filter((_, i) => i !== index) })}
                />
              </HStack>
            </SimpleGrid>
          ))}
        </Stack>
        <Button
          size="sm"
          variant="outline"
          mt="2"
          isDisabled={!isEditable}
          onClick={() => save({ prizes: [...draft.prizes, { description: "" }] })}
        >
          {t("Add prize")}
        </Button>
        {errorText("prizes") && <FormErrorMessage>{errorText("prizes")}</FormErrorMessage>}
      </FormControl>

      <FormControl mt="5" isDisabled={!isEditable} isRequired isInvalid={!!error("jury")}>
        <FormLabel fontSize="sm">{t("Jury members")}</FormLabel>
        <Text fontSize="xs" color="smMuted" mb="2">
          {t(
            "Only persons independent of the participants. Link a member to a user so they can confirm the jury's report in SourceMagnet.",
          )}
        </Text>
        <Stack spacing="3">
          {draft.jury.map((member, index) => (
            <Box key={index} borderWidth="1px" borderColor="smBorder" borderRadius="md" p="3">
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing="2">
                <Input
                  size="sm"
                  value={member.name}
                  placeholder={t("Name") ?? undefined}
                  aria-label={t("Name of jury member {{number}}", { number: index + 1 }) ?? undefined}
                  onChange={(event) => setMember(index, { name: event.target.value }, TEXT_DEBOUNCE_MS)}
                />
                <Input
                  size="sm"
                  value={member.role ?? ""}
                  placeholder={t("Role, for example chair or architect") ?? undefined}
                  aria-label={t("Role of jury member {{number}}", { number: index + 1 }) ?? undefined}
                  onChange={(event) => setMember(index, { role: event.target.value }, TEXT_DEBOUNCE_MS)}
                />
              </SimpleGrid>
              <Box mt="2">
                <PersonSelector
                  organizationIds={[buyerOrganizationId]}
                  capability={PersonSearchCapability.ViewSourcingEvent}
                  departmentId={homeDepartmentId}
                  selectedPerson={
                    member.personId ? ({ id: member.personId, firstName: member.name, lastName: "", email: "" } as PersonDto) : null
                  }
                  placeholder={t("Link to a user (optional)") ?? undefined}
                  isDisabled={!isEditable}
                  onChange={(person) =>
                    setMember(index, {
                      personId: person?.id ?? null,
                      name: person ? [person.firstName, person.lastName].filter(Boolean).join(" ") : member.name,
                    })
                  }
                />
                {errorText(`jury.${index}.personId`) && (
                  <Text fontSize="xs" color="smError" mt="1">
                    {errorText(`jury.${index}.personId`)}
                  </Text>
                )}
              </Box>
              <HStack mt="2" spacing="5" flexWrap="wrap">
                {qualificationSet && (
                  <Checkbox
                    size="sm"
                    isChecked={member.holdsRequiredQualification}
                    onChange={(event) => setMember(index, { holdsRequiredQualification: event.target.checked })}
                  >
                    {t("Holds the required qualification")}
                  </Checkbox>
                )}
                <Checkbox
                  size="sm"
                  isChecked={member.independenceConfirmed}
                  onChange={(event) => setMember(index, { independenceConfirmed: event.target.checked })}
                >
                  {t("Has confirmed independence from the participants")}
                </Checkbox>
                <Button
                  size="xs"
                  variant="ghost"
                  leftIcon={<FaTrash />}
                  isDisabled={!isEditable}
                  onClick={() => save({ jury: draft.jury.filter((_, i) => i !== index) })}
                >
                  {t("Remove")}
                </Button>
              </HStack>
            </Box>
          ))}
        </Stack>
        <Button
          size="sm"
          variant="outline"
          mt="2"
          isDisabled={!isEditable}
          onClick={() =>
            save({
              jury: [...draft.jury, { name: "", holdsRequiredQualification: false, independenceConfirmed: false }],
            })
          }
        >
          {t("Add jury member")}
        </Button>
        {qualificationSet && draft.jury.length > 0 && !holdsQualifiedThird(draft.jury) && (
          <Text fontSize="xs" color="smError" mt="2">
            {t("At least a third of the jury must hold the qualification required of the participants.")}
          </Text>
        )}
        {errorText("jury") && <FormErrorMessage>{errorText("jury")}</FormErrorMessage>}
      </FormControl>
    </Box>
  );
};
